// Fields an imported account can carry. The required ones are all a new account needs: email doubles
// as the username, the backend generates the password and the welcome email carries a one-time link
// to set it. The optional ones fill the profile when the file has them. `max` mirrors the column widths.
export const IMPORT_FIELDS = [
  { key: "email", label: "Email", required: true, aliases: ["email", "emailaddress", "e-mail", "mail"] },
  { key: "firstName", label: "First name", required: true, max: 100, aliases: ["firstname", "first", "givenname", "fname"] },
  { key: "lastName", label: "Last name", required: true, max: 100, aliases: ["lastname", "last", "surname", "familyname", "lname"] },
  { key: "phone", label: "Phone", max: 50, aliases: ["phone", "phonenumber", "mobile", "cell", "cellphone", "telephone", "tel"] },
  { key: "addressLine1", label: "Address line 1", max: 255, aliases: ["address", "addressline1", "address1", "street", "streetaddress"] },
  { key: "addressLine2", label: "Address line 2", max: 255, aliases: ["addressline2", "address2", "apt", "suite", "unit"] },
  { key: "city", label: "City", max: 100, aliases: ["city", "town"] },
  { key: "state", label: "State", max: 100, aliases: ["state", "province", "region", "st"] },
  { key: "zip", label: "ZIP", max: 20, aliases: ["zip", "zipcode", "postalcode", "postcode", "postal"] },
];

export const REQUIRED_FIELDS = IMPORT_FIELDS.filter((f) => f.required);

export const IGNORE = "";

// GLOBAL_ADMIN and STUDENT are never created through an import.
export const IMPORT_ROLES = [
  { value: "INSTRUCTOR", label: "Instructor" },
  { value: "COURSE_COORDINATOR", label: "Course Coordinator" },
  { value: "ADMIN", label: "Tenant Admin" },
  { value: "AGENCY_ADMIN", label: "Agency Admin" },
];

const MAX_EMAIL = 100;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+().\-\s/xX]+$/;

const norm = (s) => String(s ?? "").toLowerCase().replace(/[\s_.]/g, "");

/** Pre-selects a field for each header that obviously names one; everything else starts ignored. */
export function guessMapping(headers) {
  const taken = new Set();
  return headers.map((h) => {
    const n = norm(h);
    const field = IMPORT_FIELDS.find((f) => !taken.has(f.key) && f.aliases.includes(n));
    if (!field) return IGNORE;
    taken.add(field.key);
    return field.key;
  });
}

/** Required fields that still have no column mapped to them. */
export function unmappedFields(mapping) {
  return REQUIRED_FIELDS.filter((f) => !mapping.includes(f.key));
}

/** One editable record per data row, taking each field from the column mapped to it. */
export function buildRows(dataRows, mapping) {
  return dataRows.map((cells) => {
    const row = { roles: [] };
    IMPORT_FIELDS.forEach((f) => { row[f.key] = ""; });
    mapping.forEach((field, col) => {
      if (field) row[field] = String(cells[col] ?? "").trim();
    });
    return row;
  });
}

/**
 * Same rules the backend enforces, so problems show up while the admin is still editing.
 * Returns one { field: message } object per row.
 */
export function validateRows(rows, { requireRoles = false } = {}) {
  const firstRowByEmail = new Map();
  return rows.map((row, i) => {
    const errors = {};
    const email = String(row.email ?? "").trim().toLowerCase();
    if (!email) errors.email = "Email is required";
    else if (email.length > MAX_EMAIL) errors.email = `Email must be at most ${MAX_EMAIL} characters`;
    else if (!EMAIL_RE.test(email)) errors.email = "Not a valid email address";
    else if (firstRowByEmail.has(email)) errors.email = `Duplicate of row ${firstRowByEmail.get(email) + 1}`;
    else firstRowByEmail.set(email, i);

    for (const f of IMPORT_FIELDS.filter((x) => x.key !== "email")) {
      const v = String(row[f.key] ?? "").trim();
      if (!v) {
        if (f.required) errors[f.key] = `${f.label} is required`;
      } else if (v.length > f.max) {
        errors[f.key] = `${f.label} must be at most ${f.max} characters`;
      }
    }
    const phone = String(row.phone ?? "").trim();
    const digits = phone.replace(/\D/g, "").length;
    if (phone && !errors.phone && (!PHONE_RE.test(phone) || digits < 7 || digits > 20)) {
      errors.phone = "Not a valid phone number";
    }
    if (requireRoles && !(row.roles || []).length) errors.roles = "Choose at least one role";
    return errors;
  });
}
